import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useImmer } from "use-immer";
import { v4 } from "uuid";

import {
  AiChatState,
  AiChatTurn,
  ChatWorkflowRequestBodyDto,
  UiStreamAction,
} from "@intelligems/ig-types";
import { useExperimentAnalyticsChatV2 } from "@src/api/ai/ai-analytics/use-experiment-analytics-chat-v2";
import { useGetExperimentAnalyticsChatSessionHistoryV2 } from "@src/api/ai/ai-analytics/use-get-experiment-analytics-chat-session-history-v2";
import { aiExperimentAnalyticsV2Keys } from "@src/api/queryKeys";
import { ExperimentAnalyticsChatHistoryV2 } from "@src/features/ai/components/AiChat/ExperimentAnalyticsChatHistoryV2";
import { createUserMessage } from "@src/features/ai/components/AiChat/utils/workflowUtils";
import { ExecutionPanel } from "@src/features/ai/components/WorkflowEditor/execution/ExecutionPanel";
import {
  EXPERIMENT_ANALYTICS_SUMMARY_CHAT_WORKFLOW_ID,
  V2Summary,
} from "@src/features/ai/components/usages/useAnalyticsSummaryV2";
import {
  clearAiChat,
  getAiChat,
  handleAiChatAction,
  restoreChatState,
} from "@src/store/slices/aiChat";
import { getAiChatProps } from "@src/store/slices/global";
import { useQueryClient } from "@tanstack/react-query";
import { RateConversation } from "@src/features/ai/components/WorkflowEditor/execution/components/RateConversation";
import { OnAiChatSend } from "@src/features/ai/components/WorkflowEditor/execution/types";

interface AiAnalyticsChatProps {
  onClose?: () => void;
}

// Stable ids for the synthetic seed turn. Same in new-chat setup and restore
// paths so rendering is identical; never collide with real turns persisted by
// the backend.
const SUMMARY_SEED_TURN_ID = "summary-seed-turn";
const SUMMARY_SEED_NODE_ID = "summary-seed";
const SUMMARY_SEED_MESSAGE_ID = "summary-seed-message";

type ChatMode = "new" | "loading" | "ready";

const buildSummarySeedTurn = (summaryText: string): AiChatTurn => {
  const timestamp = Date.now();
  return {
    turnId: SUMMARY_SEED_TURN_ID,
    nodes: [
      {
        id: SUMMARY_SEED_NODE_ID,
        name: "Initial Summary",
        status: "finished",
        messages: [
          {
            id: SUMMARY_SEED_MESSAGE_ID,
            role: "assistant",
            timestamp,
            parts: [
              {
                id: `${SUMMARY_SEED_MESSAGE_ID}-part`,
                type: "text",
                text: summaryText,
                timestamp,
              },
            ],
          },
        ],
      },
    ],
  };
};

const readSummaryFromInitialContext = (
  initialContext: unknown
): string | undefined => {
  if (!initialContext || typeof initialContext !== "object") return undefined;
  const variables = (initialContext as { variables?: unknown }).variables;
  if (!variables || typeof variables !== "object") return undefined;
  const summary = (variables as { summary?: unknown }).summary;
  return typeof summary === "string" ? summary : undefined;
};

export const AiAnalyticsChat = ({ onClose }: AiAnalyticsChatProps) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const aiChat = useSelector(getAiChat);
  const { initialContext: summary } = useSelector(getAiChatProps<V2Summary>);
  const { chatWorkflow } = useExperimentAnalyticsChatV2();

  // Single source of truth for the initialise/restore branch. No racing
  // effects: "new" seeds an empty chat from `summary`; "loading" awaits server
  // history and dispatches restoreChatState with the synthetic summary turn
  // prepended; "ready" parks until onSessionIdChange.
  const [mode, setMode] = useImmer<ChatMode>("new");
  const [isNewChat, setIsNewChat] = useImmer(true);
  const [showRating, setShowRating] = useImmer(false);
  const [loadedSessionId, setLoadedSessionId] = useImmer<string | null>(null);

  const sessionHistory = useGetExperimentAnalyticsChatSessionHistoryV2({
    sessionId: loadedSessionId,
    enabled: mode === "loading" && !!loadedSessionId,
  });

  const setSessionId = (sessionId: string) =>
    dispatch(
      handleAiChatAction({
        type: "setSessionId",
        sessionId,
      })
    );

  // Ensure the redux session has an id before any send happens.
  useEffect(() => {
    if (!aiChat.sessionId) {
      setSessionId(v4());
    }
  }, [aiChat.sessionId]);

  const seedNewChat = (sessionId: string) => {
    if (!summary) return;
    const actions: UiStreamAction[] = [
      {
        type: "initialize",
        workflowId: EXPERIMENT_ANALYTICS_SUMMARY_CHAT_WORKFLOW_ID,
        sessionId,
        nodeId: v4(),
      },
      {
        type: "addMessage",
        nodeId: SUMMARY_SEED_NODE_ID,
        nodeName: "Initial Summary",
        turnId: SUMMARY_SEED_TURN_ID,
        message: {
          id: SUMMARY_SEED_MESSAGE_ID,
          role: "assistant",
          timestamp: Date.now(),
          parts: [
            {
              id: `${SUMMARY_SEED_MESSAGE_ID}-part`,
              type: "text",
              text: summary.data.summary,
              timestamp: Date.now(),
              // @ts-expect-error legacy state field for stream-rendering parts
              state: "immediate",
            },
          ],
        },
      },
      {
        type: "setNextAction",
        nextAction: {
          type: "user_input",
          variablePath: "",
          userInput: {
            type: "choice",
            options: summary.data.questions.map((q) => ({
              value: q,
              label: q,
            })),
          },
        },
      },
    ];

    dispatch(handleAiChatAction(actions));
  };

  // Single mode-driven effect: branches on `mode` and runs the matching
  // setup/restore logic exactly once before transitioning to "ready".
  useEffect(() => {
    if (mode === "new" && summary && aiChat.sessionId) {
      seedNewChat(aiChat.sessionId);
      setIsNewChat(true);
      setMode("ready");
    } else if (
      mode === "loading" &&
      sessionHistory.isSuccess &&
      sessionHistory.data
    ) {
      const seedSummary = readSummaryFromInitialContext(
        sessionHistory.data.initialContext
      );
      const restoredState: AiChatState = seedSummary
        ? {
            ...sessionHistory.data,
            turns: [
              buildSummarySeedTurn(seedSummary),
              ...sessionHistory.data.turns,
            ],
          }
        : sessionHistory.data;
      dispatch(restoreChatState(restoredState));
      setLoadedSessionId(null);
      setMode("ready");
    }
  }, [
    mode,
    summary,
    aiChat.sessionId,
    sessionHistory.isSuccess,
    sessionHistory.data,
  ]);

  const onSessionIdChange = (id: string) => {
    dispatch(clearAiChat());
    if (id === "new") {
      const newId = v4();
      setSessionId(newId);
      setIsNewChat(true);
      setMode("new");
    } else {
      setIsNewChat(false);
      setSessionId(id);
      setLoadedSessionId(id);
      setMode("loading");
    }
  };

  const onSend: OnAiChatSend = {
    chat: (payload: ChatWorkflowRequestBodyDto) => {
      if (!summary || !payload.input) return;

      setShowRating(true);

      // Generate a stable id for the user UiMessage. The same id flows through
      // `inputMessageId` so the backend stamps it on its echo via
      // `addMessage`; the reducer reconciles by id and replaces the optimistic
      // entry in place rather than duplicating it.
      const inputMessageId = v4();
      const { message } = createUserMessage(payload.input);
      const nodeId = v4();
      const turnId = v4();

      if (message) {
        message.id = inputMessageId;
        dispatch(
          handleAiChatAction([
            {
              type: "addMessage",
              turnId,
              message,
              nodeId,
              nodeName: "User Input",
            },
          ])
        );
      }

      // The llm-chat user prompt template uses {{data.turnInput.content}},
      // which is only populated for text-type inputs. Convert choice selections
      // to text so the LLM receives the question text.
      const workflowInput =
        payload.input?.type === "choice"
          ? { type: "text" as const, content: payload.input.choice }
          : payload.input;

      const shouldStartWorkflow = isNewChat;

      if (shouldStartWorkflow) {
        setIsNewChat(false);

        void chatWorkflow
          .mutate({
            workflowId: EXPERIMENT_ANALYTICS_SUMMARY_CHAT_WORKFLOW_ID,
            sessionId: aiChat.sessionId,
            inputMessageId,
            initialContext: {
              variables: {
                summary: summary.data.summary,
                experience_id: summary.payload.experience_id,
                experienceStatus: summary.payload.experienceStatus,
                overall_variation_overview:
                  summary.payload.overall_variation_overview,
                metric_definitions: summary.payload.metric_definitions,
              },
              metadata: {
                experienceId: summary.payload.experience_id,
              },
            },
            input: workflowInput,
          })
          .then(() => {
            void queryClient.invalidateQueries({
              queryKey: aiExperimentAnalyticsV2Keys.chatHistory(
                summary.payload.experience_id
              ),
            });
          });
      } else {
        void chatWorkflow.mutate({
          sessionId: aiChat.sessionId,
          inputMessageId,
          input: workflowInput,
        });
      }
    },
    isPending: chatWorkflow.isPending,
  };

  const handleClose = () => {
    if (onClose) {
      onClose();
    }
  };

  return (
    <ExecutionPanel
      onClose={handleClose}
      onSend={onSend}
      feature={"analytics"}
      workflowId={EXPERIMENT_ANALYTICS_SUMMARY_CHAT_WORKFLOW_ID}
      showSystemMessages={false}
      showToolCallOutput={false}
      clearReasoningOnMessage={true}
      slots={{
        headerLeft: (
          <ExperimentAnalyticsChatHistoryV2
            currentSessionId={aiChat.sessionId ?? ""}
            onSessionIdChange={onSessionIdChange}
            experienceId={summary?.payload.experience_id}
          />
        ),
        promptInputTools: (
          <>{showRating && <RateConversation feedbackTarget="ai" />}</>
        ),
      }}
    />
  );
};
